import { FacilityCategoryIcon } from '@/components/feature/facility/facility-category-icon';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import { useConfigStore } from '@/stores';
import type { FacilityCategoryItem } from '@/types/facility-category';
import clsx from 'clsx';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface FacilityCategoryCarouselItemProps {
    content: FacilityCategoryItem;
    tabIndex: number;
    onClick?: (data: FacilityCategoryItem) => void;
}

const FacilityCategoryCarouselItem = memo(({ content, tabIndex, onClick }: FacilityCategoryCarouselItemProps) => {
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const parent = e.currentTarget;
            parent.classList.add('poi-active');
            handleInteraction(e, () => onClick?.(content));
        },
        [handleInteraction, onClick, content]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => handleKeyboardInteraction(e, () => onClick?.(content)),
        [handleKeyboardInteraction, onClick, content]
    );

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            key={content.id}
            tabIndex={tabIndex}
            data-interactive
            data-playsound="ting"
            data-role={t('common.button.tts.popup_button')}
            className={clsx(styles.wrapper, {
                [styles['wrapper__horizontal--halfmode']]: isHorizontal && screenMode === 'split',
                [styles['wrapper__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                [styles['wrapper__vertical']]: !isHorizontal,
            })}
            aria-label={content.name}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div className={styles['wrapper__body']}>
                <div
                    className={clsx(styles['wrapper__image'], {
                        [styles['wrapper__image__contrast']]: contrast,
                    })}
                >
                    <FacilityCategoryIcon
                        clientCode={content.clientCode}
                        contrast={contrast}
                        isHalfMode={screenMode === 'split'}
                    />
                </div>
                <h3
                    className={clsx(styles['wrapper__name'], {
                        [styles['wrapper__name__contrast']]: contrast,
                        [styles['wrapper__name--halfmode']]: screenMode === 'split',
                    })}
                >
                    {content.name.length > 40 ? `${content.name.slice(0, 40)}...` : content.name}
                </h3>
            </div>
        </div>
    );
});

FacilityCategoryCarouselItem.displayName = 'FacilityCategoryCarouselItem';

export default FacilityCategoryCarouselItem;
